import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

// Define the props for the LocationMap component
interface LocationMapProps {
  latitude: number;
  longitude: number;
  height?: string;
  zoom?: number;
  radius?: number; // Radius in meters for the geofence circle
  onLocationChange?: (lat: number, lng: number) => void;
  canAddMarkers?: boolean; // Contrôle si les markers peuvent être ajoutés
  showCircleOnLoad?: boolean; // Contrôle si le cercle doit être affiché dès le chargement
  showMarker?: boolean; // Contrôle si le marker doit être affiché
}

const LocationMap: React.FC<LocationMapProps> = ({
  latitude,
  longitude,
  height = '400px',
  zoom = 13,
  radius = 100,
  onLocationChange,
  canAddMarkers = true, // Par défaut, les markers peuvent être ajoutés
  showCircleOnLoad = true, // Par défaut, afficher le cercle dès le chargement
  showMarker = true, // Par défaut, afficher le marker
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);
  const circleRef = useRef<maplibregl.GeoJSONSource | null>(null);
  const circleLayerId = 'geofence-radius-circle';
  const circleSourceId = 'geofence-radius-source';

  // Use state to track the current coordinates to force re-renders
  const [currentCoords, setCurrentCoords] = useState<[number, number]>([longitude, latitude]);
  const [currentRadius, setCurrentRadius] = useState<number>(radius);

  // Ensure map is initialized
  const [mapInitialized, setMapInitialized] = useState(false);

  // Debug state to track circle visibility
  const [circleCreated, setCircleCreated] = useState(false);

  // State to track if user has interacted with the map
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // Calculate the appropriate zoom level based on the radius
  const calculateZoomLevel = (radiusInMeters: number): number => {
    // Approximate calculation - smaller radius = higher zoom level
    // This is a simple formula, adjust as needed for better results
    const baseZoom = 15;

    if (radiusInMeters <= 50) return baseZoom + 2;
    if (radiusInMeters <= 100) return baseZoom + 1;
    if (radiusInMeters <= 200) return baseZoom;
    if (radiusInMeters <= 500) return baseZoom - 1;
    if (radiusInMeters <= 1000) return baseZoom - 2;
    if (radiusInMeters <= 2000) return baseZoom - 3;
    if (radiusInMeters <= 5000) return baseZoom - 4;

    // For very large radii
    return baseZoom - 7;
  };

  // Update state when props change
  useEffect(() => {
    if (longitude !== currentCoords[0] || latitude !== currentCoords[1]) {
      setCurrentCoords([longitude, latitude]);

      // Si on ne veut pas afficher de marker, on s'assure qu'il n'y en a pas
      if (marker.current && map.current && (!showMarker)) {
        marker.current.remove();
        marker.current = null;
      }

      // Update the circle when coordinates change
      // et marquer que l'utilisateur a interagi via autocomplete
      setUserHasInteracted(true);
      updateGeofenceCircle([longitude, latitude], currentRadius);
    }
  }, [latitude, longitude, currentCoords, showMarker]);

  // Update circle when radius changes
  useEffect(() => {
    if (radius !== currentRadius) {
      setCurrentRadius(radius);

      if (userHasInteracted || showCircleOnLoad) {
        updateGeofenceCircle(currentCoords, radius);
      }

      // Update the zoom level based on the new radius
      if (map.current && mapInitialized) {
        const newZoom = calculateZoomLevel(radius);
        map.current.flyTo({
          center: currentCoords,
          zoom: newZoom,
          essential: true,
          duration: 500
        });
      }
    }
  }, [radius, currentRadius, userHasInteracted, showCircleOnLoad]);

  // Function to create a circle GeoJSON around a point
  const createCircleGeoJSON = (center: [number, number], radiusInMeters: number) => {
    // Earth's radius in meters
    const earthRadius = 6378137;

    // Convert radius from meters to degrees (approximate)
    // This is a simplification - accurate enough for small distances
    const radiusInDegrees = (radiusInMeters / earthRadius) * (180 / Math.PI);

    // Create a simple circle with 64 points
    const points = 64;
    const coordinates: Array<[number, number]> = [];

    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const x = center[0] + radiusInDegrees * Math.cos(angle) / Math.cos(center[1] * Math.PI / 180);
      const y = center[1] + radiusInDegrees * Math.sin(angle);
      coordinates.push([x, y]);
    }

    // Close the circle
    coordinates.push(coordinates[0]);

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates]
      },
      properties: {}
    };
  };

  // Function to update or create the geofence circle
  const updateGeofenceCircle = (center: [number, number], radiusInMeters: number) => {
    if (!map.current || !mapInitialized) return;

    const circleGeoJSON = createCircleGeoJSON(center, radiusInMeters);

    // Check if source exists
    if (map.current.getSource(circleSourceId)) {
      // Update existing source
      const source = map.current.getSource(circleSourceId) as maplibregl.GeoJSONSource;
      source.setData(circleGeoJSON as any);
      circleRef.current = source;
      setCircleCreated(true);
    } else {
      // Create new source and layer
      try {
        map.current.addSource(circleSourceId, {
          type: 'geojson',
          data: circleGeoJSON as any
        });

        circleRef.current = map.current.getSource(circleSourceId) as maplibregl.GeoJSONSource;

        map.current.addLayer({
          id: circleLayerId,
          type: 'fill',
          source: circleSourceId,
          paint: {
            'fill-color': '#ff0000',
            'fill-opacity': 0.3, // Increased from 0.2 for better visibility
            'fill-outline-color': '#ff0000'
          }
        });

        // Also add an outline layer for better visibility
        map.current.addLayer({
          id: `${circleLayerId}-outline`,
          type: 'line',
          source: circleSourceId,
          paint: {
            'line-color': '#ff0000',
            'line-width': 3 // Increased from 2 for better visibility
          }
        });

        setCircleCreated(true);
      } catch (error) {
        console.error("Error creating geofence circle:", error);
      }
    }
  };

  // Initialize map
  useEffect(() => {
    if (!map.current && mapContainer.current) {
      try {
        // Calculate initial zoom based on radius
        const initialZoom = calculateZoomLevel(currentRadius);

        // Initialize the map
        map.current = new maplibregl.Map({
          container: mapContainer.current,
          style: 'https://openmaptiles.geo.data.gouv.fr/styles/osm-bright/style.json',
          center: currentCoords,
          zoom: initialZoom,
        });

        // Add navigation controls
        map.current.addControl(new maplibregl.NavigationControl());

        // Ensure map is properly sized after initialization
        map.current.on('load', () => {
          if (map.current) {
            map.current.resize();
            setMapInitialized(true);

            // Add the initial geofence circle seulement si showCircleOnLoad est true
            if (showCircleOnLoad) {
              setTimeout(() => {
                // Use a slight delay to ensure the map is fully loaded
                updateGeofenceCircle(currentCoords, currentRadius);
              }, 500);
            }

            // Add initial marker only if markers can be added and should be shown
            if (canAddMarkers && showMarker && map.current && onLocationChange) {
              setTimeout(() => {
                if (!marker.current && map.current) {
                  marker.current = new maplibregl.Marker({
                    draggable: true,
                    color: '#FF0000'
                  })
                    .setLngLat(currentCoords)
                    .addTo(map.current);

                  // Add drag event listener to the marker
                  if (marker.current) {
                    marker.current.on('dragend', () => {
                      const lngLat = marker.current?.getLngLat();
                      if (lngLat) {
                        setUserHasInteracted(true);
                        onLocationChange(lngLat.lat, lngLat.lng);

                        // Update the circle when marker is dragged
                        updateGeofenceCircle([lngLat.lng, lngLat.lat], currentRadius);
                      }
                    });
                  }
                }
              }, 500);
            }
          }
        });

        // Add click event to the map - after the map is loaded
        map.current.on('click', (e: maplibregl.MapMouseEvent) => {
          // Si on ne peut pas ajouter de markers, on ne fait rien
          if (!canAddMarkers) {
            return;
          }

          // On indique que l'utilisateur a interagi avec la carte
          setUserHasInteracted(true);

          // Get the clicked coordinates
          const clickedLng = e.lngLat.lng;
          const clickedLat = e.lngLat.lat;

          // Remove existing marker if any
          if (marker.current) {
            marker.current.remove();
            marker.current = null;
          }

          try {
            // Create a new marker at the clicked position, only if showMarker is true
            if (showMarker) {
              marker.current = new maplibregl.Marker({
                draggable: true,
                color: '#FF0000' // Red color to make it more visible
              })
                .setLngLat([clickedLng, clickedLat])
                .addTo(map.current!);

              // Add drag end event listener to the marker
              if (marker.current && onLocationChange) {
                marker.current.on('dragend', () => {
                  const lngLat = marker.current?.getLngLat();
                  if (lngLat) {
                    onLocationChange(lngLat.lat, lngLat.lng);

                    // Update the circle when marker is dragged
                    updateGeofenceCircle([lngLat.lng, lngLat.lat], currentRadius);
                  }
                });
              }
            }

            // Update the coordinates via callback, even if no marker is shown
            if (onLocationChange) {
              onLocationChange(clickedLat, clickedLng);

              // Update the circle when new coordinates are selected
              updateGeofenceCircle([clickedLng, clickedLat], currentRadius);
            }
          } catch (error) {
            console.error("Error creating/handling marker:", error);
          }
        });
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    }

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        marker.current = null;
        circleRef.current = null;
        setMapInitialized(false);
      }
    };
  }, [canAddMarkers, showCircleOnLoad, showMarker]);

  // Update map center when coordinates change (but don't add a marker)
  useEffect(() => {
    if (map.current && mapInitialized) {
      // Calculate the appropriate zoom level based on radius
      const newZoom = calculateZoomLevel(currentRadius);

      // Update map center with animation and zoom
      map.current.flyTo({
        center: currentCoords,
        zoom: newZoom,
        essential: true,
        duration: 1000
      });

      // Ensure circle is updated only if user has interacted or showCircleOnLoad is true
      if (userHasInteracted || showCircleOnLoad) {
        setTimeout(() => {
          updateGeofenceCircle(currentCoords, currentRadius);
        }, 1000);
      }
    }
  }, [currentCoords, mapInitialized, userHasInteracted, showCircleOnLoad]);

  // Ensure the circle is created if it wasn't
  useEffect(() => {
    if (map.current && mapInitialized && !circleCreated && currentRadius > 0 && (userHasInteracted || showCircleOnLoad)) {
      updateGeofenceCircle(currentCoords, currentRadius);
    }
  }, [mapInitialized, circleCreated, currentRadius, userHasInteracted, showCircleOnLoad]);

  // Force a re-render of the circle when the component mounts
  useEffect(() => {
    if (map.current && mapInitialized && (userHasInteracted || showCircleOnLoad)) {
      updateGeofenceCircle(currentCoords, currentRadius);
    }
  }, [userHasInteracted, showCircleOnLoad]);

  return (
    <div ref={mapContainer} style={{ height, width: '100%' }} />
  );
};

export default LocationMap;
